import { format, parseISO, isValid } from 'date-fns';

export type EntityDateFormat = 'DD-MM-YYYY' | 'MM-DD-YYYY' | 'YYYY-MM-DD';

// Map the entity setting to a date-fns pattern.
const PATTERN: Record<EntityDateFormat, string> = {
  'DD-MM-YYYY': 'dd-MM-yyyy',
  'MM-DD-YYYY': 'MM-dd-yyyy',
  'YYYY-MM-DD': 'yyyy-MM-dd',
};

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : typeof value === 'string' ? parseISO(value) : new Date(value);
  return isValid(d) ? d : null;
}

// Date only, per the entity setting. Invalid/empty -> '—' (em-dash, matches
// the app-wide table empty style).
export function formatDate(
  value: string | number | Date | null | undefined,
  dateFormat: EntityDateFormat = 'DD-MM-YYYY',
): string {
  const d = toDate(value);
  return d ? format(d, PATTERN[dateFormat]) : '—';
}

// Date + 24h time. Time part is not governed by the setting.
export function formatDateTime(
  value: string | number | Date | null | undefined,
  dateFormat: EntityDateFormat = 'DD-MM-YYYY',
): string {
  const d = toDate(value);
  return d ? format(d, `${PATTERN[dateFormat]} HH:mm`) : '—';
}

// Display-format a code string that embeds a launch date, e.g. the audit
// program-instance code "APT-3 - 2026-01-01" (built by the backend launch
// endpoint as `{template code} - {start_date}`). The trailing ISO date is
// rendered per the entity setting; a literal "None" prefix (artifact of
// legacy NULL template codes) is hidden. Non-matching codes pass through.
// `fmt` is the bound formatter from useEntitySettings().formatDate.
export function formatCodeDate(
  code: string | null | undefined,
  fmt: (v: string) => string,
): string | null {
  if (!code) return null;
  const m = code.match(/^(.*?)\s*-\s*(\d{4}-\d{2}-\d{2})$/);
  if (!m) return code;
  const prefix = m[1] && m[1] !== 'None' ? `${m[1]} - ` : '';
  return `${prefix}${fmt(m[2])}`;
}
